import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axios';
import './ForgotPasswordPage.css';
import logo from '../assets/teesergolf-logo.png';
import Navigation from '../components/Navigation';

// Steps: 'email' -> 'code' -> 'password' -> 'success'
function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setIsLoading(true);
    try {
      await axiosInstance.post('/auth/forgot-password', { email });
      setStep('code');
    } catch (err) {
      console.error('Error requesting password reset:', err);
      // Show generic message for security (don't reveal if email exists)
      if (err.response?.status === 400 && err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        // Still move to code step even if email doesn't exist (security best practice)
        // Backend should handle this gracefully
        setStep('code');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!code.trim()) {
      setError('Please enter the verification code');
      return;
    }

    if (code.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setIsLoading(true);
    try {
      await axiosInstance.post('/auth/verify-reset-code', { email, code });
      setStep('password');
    } catch (err) {
      console.error('Error verifying code:', err);
      setError(err.response?.data?.message || 'Invalid or expired code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!newPassword) {
      setError('Please enter a new password');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      await axiosInstance.post('/auth/reset-password', {
        email,
        code,
        newPassword
      });
      setStep('success');
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setIsLoading(true);
    try {
      await axiosInstance.post('/auth/forgot-password', { email });
      setError(''); // Clear any previous errors
      alert('A new code has been sent to your Telegram.');
    } catch (err) {
      console.error('Error resending code:', err);
      setError('Failed to resend code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="forgot-password-page">
      <Navigation />
      <div className="forgot-password-container">
        <div className="forgot-password-logo">
          <img src={logo} alt="TeeserGolf" />
        </div>

        {step === 'email' && (
          <>
            <h1>Forgot Password</h1>
            <p className="forgot-password-subtitle">
              Enter your email address and we'll send a verification code to your linked Telegram account.
            </p>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleEmailSubmit} className="forgot-password-form">
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  disabled={isLoading}
                  autoComplete="email"
                />
              </div>

              <button type="submit" className="submit-button" disabled={isLoading}>
                {isLoading ? 'Sending...' : 'Send Verification Code'}
              </button>
            </form>

            <div className="back-to-login">
              <Link to="/login">Back to Login</Link>
            </div>
          </>
        )}

        {step === 'code' && (
          <>
            <h1>Enter Verification Code</h1>
            <p className="forgot-password-subtitle">
              We've sent a 6-digit code to your Telegram. Enter it below to continue.
            </p>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleCodeSubmit} className="forgot-password-form">
              <div className="form-group">
                <label htmlFor="code">Verification Code</label>
                <input
                  type="text"
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  disabled={isLoading}
                  maxLength={6}
                  className="code-input"
                  autoComplete="one-time-code"
                />
              </div>

              <button type="submit" className="submit-button" disabled={isLoading}>
                {isLoading ? 'Verifying...' : 'Verify Code'}
              </button>
            </form>

            <div className="resend-code">
              Didn't receive the code?{' '}
              <button
                type="button"
                className="resend-link"
                onClick={handleResendCode}
                disabled={isLoading}
              >
                Resend Code
              </button>
            </div>

            <div className="back-to-login">
              <Link to="/login">Back to Login</Link>
            </div>
          </>
        )}

        {step === 'password' && (
          <>
            <h1>Set New Password</h1>
            <p className="forgot-password-subtitle">
              Enter your new password below.
            </p>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handlePasswordSubmit} className="forgot-password-form">
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  disabled={isLoading}
                  autoComplete="new-password"
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  disabled={isLoading}
                  autoComplete="new-password"
                />
              </div>

              <button type="submit" className="submit-button" disabled={isLoading}>
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>

            <div className="back-to-login">
              <Link to="/login">Back to Login</Link>
            </div>
          </>
        )}

        {step === 'success' && (
          <>
            <div className="success-icon">&#10003;</div>
            <h1>Password Reset!</h1>
            <p className="forgot-password-subtitle">
              Your password has been successfully reset. You can now log in with your new password.
            </p>

            <button
              className="submit-button"
              onClick={() => navigate('/login')}
            >
              Go to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
